import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { useHistory } from 'react-router-dom';
import { Row, Col, Dropdown, Grid, Button, Tooltip } from 'antd';
import {
	MenuOutlined,
	EditOutlined,
	DeleteOutlined,
	ZoomInOutlined,
	DownloadOutlined,
	PlusCircleOutlined
} from '@ant-design/icons';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useGetResolution886Report } from '@/hooks/v2/resolution-886';
import { ToastError } from '../Resolution886/components/ToastError';
import { TOAST_DURATION_RESOLUTION_886 } from '@/utils/constants';
import { ModalConfirmReview } from './ModalConfirmReview';
import { ReviewVersionItem } from '@/types/Resolution886';
import { useDownloadPDF } from './hooks/useDownloadPDF';
import { ModalReviews } from './ModalReviews';
import { useReviewsModal } from './hooks';
import type { SelectedReport } from './types';
import * as S from './styles';

interface ActionsProps {
	rowData: any;
	onDelete: (report: SelectedReport) => void;
}

const REPORT_PATH = '/custom-reports/resolution-886/report';
const BREAKPOINT_XXL_FONT_SIZE = '25px';
const BREAKPOINT_XXL_BUTTON_SIZE = 'large';
const BREAKPOINT_SM_FONT_SIZE = '15px';
const BREAKPOINT_SM_BUTTON_SIZE = 'small';

export function Actions({ rowData, onDelete }: Readonly<ActionsProps>): JSX.Element {
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
	const [isToastErrorOpen, setIsToastErrorOpen] = useState<boolean>(false);
	const [toastMessage, setToastMessage] = useState<string>('');
	const { report, isOpenModalReviews, handleOpenModalReviews, handleCloseModalReviews } = useReviewsModal();

	const history = useHistory();
	const breakpoints = Grid.useBreakpoint();
	const { organization, company } = useApplicationContext();

	const { data: reportData } = useGetResolution886Report({
		organization_id: organization?.id!,
		company_id: company?.id!,
		id: rowData.id
	});

	const lastReviewId = (() => {
		if (!reportData?.reviews || reportData.reviews.length === 0) return null;

		const sortedReviews = reportData.reviews.sort((a: any, b: any) => {
			const dateA = new Date(a.updated_at || a.created_at);
			const dateB = new Date(b.updated_at || b.created_at);
			return dateA.getTime() - dateB.getTime();
		});

		return sortedReviews[sortedReviews.length - 1]?.id || null;
	})();

	const { onDownloadPDF, downloadPDFLoading } = useDownloadPDF({
		report: rowData,
		organizationId: organization?.id!,
		companyId: company?.id!,
		lastReviewId: lastReviewId || ''
	});

	const fontSize = breakpoints.xxl ? BREAKPOINT_XXL_FONT_SIZE : BREAKPOINT_SM_FONT_SIZE;
	const buttonSize = breakpoints.xxl ? BREAKPOINT_XXL_BUTTON_SIZE : BREAKPOINT_SM_BUTTON_SIZE;

	function handleOpenToastError(message: string): void {
		setToastMessage(message);
		setIsToastErrorOpen(true);

		setTimeout(() => {
			setIsToastErrorOpen(false);
		}, TOAST_DURATION_RESOLUTION_886);
	}

	function openCreateVersionModal(): void {
		if (!rowData.last_review_consolidated) {
			handleOpenToastError(I18n.get('You must consolidate the last review before creating a new version'));
			return;
		}

		setIsConfirmModalOpen(true);
	}

	function closeCreateVersionModal(): void {
		setIsConfirmModalOpen(false);
	}

	function navigateToCreateVersion(report: ReviewVersionItem): void {
		closeCreateVersionModal();
		history.push(`${REPORT_PATH}/${report.id}?newReview=true`);
	}

	function navigateToEditReport(report: ReviewVersionItem): void {
		history.push(`${REPORT_PATH}/${report.id}`);
	}

	async function downloadReportPdf(): Promise<void> {
		if (!rowData.last_review_consolidated) {
			handleOpenToastError(I18n.get('You must consolidate the last review before downloading the PDF'));
			return;
		}

		try {
			await onDownloadPDF({
				pdf_custom: []
			});
		} catch (error) {
			handleOpenToastError(I18n.get('Error downloading PDF. Please try again.'));
		}
	}

	function handleOpenDeleteModal(report: ReviewVersionItem): void {
		onDelete({
			id: report.id,
			report_name: report.name
		});
	}

	const dropdownMenuItems = [
		{
			key: 'create',
			icon: <PlusCircleOutlined style={{ fontSize: '12px' }} />,
			label: (
				<Button type="link" onClick={openCreateVersionModal}>
					{I18n.get('Create new version')}
				</Button>
			)
		},
		{
			key: 'view',
			icon: <ZoomInOutlined style={{ fontSize: '12px' }} />,
			label: (
				<Button type="link" onClick={() => handleOpenModalReviews(rowData)}>
					{I18n.get('View versions')}
				</Button>
			)
		},
		{
			key: 'edit',
			icon: <EditOutlined style={{ fontSize: '12px' }} />,
			label: (
				<Button type="link" onClick={() => navigateToEditReport(rowData)}>
					{I18n.get('Edit')}
				</Button>
			)
		},
		{
			key: 'download',
			icon: <DownloadOutlined style={{ fontSize: '12px' }} />,
			label: (
				<Button
					type="link"
					loading={downloadPDFLoading}
					disabled={downloadPDFLoading}
					onClick={() => downloadReportPdf()}
				>
					{I18n.get('Download PDF')}
				</Button>
			)
		},
		{
			key: 'delete',
			danger: true,
			icon: <DeleteOutlined style={{ color: 'var(--ant-color-error)' }} />,
			label: (
				<S.CustomButton>
					<Button type="link" onClick={() => handleOpenDeleteModal(rowData)}>
						{I18n.get('Delete')}
					</Button>
				</S.CustomButton>
			)
		}
	];

	const actionButtons = [
		{
			key: 'create',
			icon: <PlusCircleOutlined style={{ fontSize }} />,
			tooltip: I18n.get('Create new version'),
			onClick: openCreateVersionModal
		},
		{
			key: 'view',
			icon: <ZoomInOutlined style={{ fontSize }} />,
			tooltip: I18n.get('View versions'),
			onClick: () => handleOpenModalReviews(rowData)
		},
		{
			key: 'edit',
			icon: <EditOutlined style={{ fontSize }} />,
			tooltip: I18n.get('Edit'),
			onClick: () => navigateToEditReport(rowData)
		},
		{
			key: 'download',
			icon: <DownloadOutlined style={{ fontSize }} />,
			tooltip: I18n.get('Download PDF'),
			onClick: downloadReportPdf,
			loading: downloadPDFLoading,
			disabled: downloadPDFLoading
		},
		{
			key: 'delete',
			icon: <DeleteOutlined style={{ color: '#D13342', fontSize }} />,
			tooltip: I18n.get('Delete'),
			onClick: () => handleOpenDeleteModal(rowData)
		}
	];

	return (
		<>
			{isToastErrorOpen && <ToastError message={toastMessage} />}
			<Row align="middle" justify="center">
				<S.ActionsInline xs={24}>
					<Row align="middle" justify="center" gutter={[10, 0]}>
						{actionButtons.map(({ key, icon, tooltip, onClick, loading, disabled }) => (
							<Col key={key} span={4}>
								<Button
									type="link"
									size={buttonSize}
									onClick={onClick}
									loading={loading}
									disabled={disabled}
									icon={
										<Tooltip overlayStyle={{ position: 'fixed' }} title={tooltip}>
											{icon}
										</Tooltip>
									}
								/>
							</Col>
						))}
					</Row>
				</S.ActionsInline>
				<S.ActionsMenu xs={2}>
					<Dropdown menu={{ items: dropdownMenuItems }} trigger={['click']}>
						<Button type="link" icon={<MenuOutlined />} />
					</Dropdown>
				</S.ActionsMenu>
			</Row>
			<ModalConfirmReview
				id={rowData.file_id}
				open={isConfirmModalOpen}
				onCancel={closeCreateVersionModal}
				onConfirm={() => navigateToCreateVersion(rowData)}
			/>
			<ModalReviews open={isOpenModalReviews} onCancel={handleCloseModalReviews} report={report} />
		</>
	);
}
